import React, {
  FunctionComponent, useCallback, useEffect, useRef, useState,
} from 'react';
import { Box, Flex } from 'rebass/styled-components';
import {
  Loading, CopyableText, Button, ConfirmModal,
} from '..';
import api from '../../api';
import { useAppState, useNotification } from '../../hooks';
import { ConfirmModalProps } from '../modal/confirm-modal';

const copyText = require('./copy-text.json');

const {
  MAVEN_API_KEY_REFRESH_CONFIRM_DETAIL, MAVEN_API_KEY_PLACEHOLDER,
  MAVEN_API_KEY_GENERATE, MAVEN_API_KEY_REGENERATE,
} = copyText;

const MavenApiKey: FunctionComponent<{}> = () => {
  const { accountId } = useAppState();
  const isMounted = useRef<boolean>(false);
  const { addNotification } = useNotification();
  const [key, setKey] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCreatingKey, setIsCreatingKey] = useState<boolean>(false);
  const [confirmModalProps, setConfirmModalProps] = useState<ConfirmModalProps>({ show: false });

  const confirmRegenerateKey = useCallback(async () => {
    if (isMounted.current) {
      setConfirmModalProps({
        show: false,
      });
      setIsLoading(true);
      try {
        const data = await api.mavenKeys.regenerateAPIKey(accountId, key);
        if (isMounted.current && data.key) {
          setKey(data.key);
        }
      } catch (error) {
        addNotification(error, 'error');
      }
      setIsLoading(false);
    }
    try {
      await api.mavenConfig.setServerToServerAuthStatus(accountId, true);
    } catch (error) {
      // do nothing
    }
  }, [accountId, addNotification, key]);

  const regenerateKey = useCallback(
    () => {
      if (!isLoading) {
        setConfirmModalProps({
          show: true,
          detail: MAVEN_API_KEY_REFRESH_CONFIRM_DETAIL,
          onConfirm: confirmRegenerateKey,
          onCancel: () => {
            if (isMounted.current) {
              setConfirmModalProps({
                show: false,
              });
            }
          },
        });
      }
    }, [confirmRegenerateKey, isLoading],
  );

  const getKey = useCallback(
    async () => {
      if (isMounted.current) {
        setIsLoading(true);
      }
      try {
        const data = await api.mavenKeys.getAPIKeys(accountId);
        if (isMounted.current && data.length) {
          setKey(data[0].key);
        }
      } catch (error) {
        addNotification(error, 'error');
      }
      if (isMounted.current) {
        setIsLoading(false);
      }
    }, [accountId, addNotification],
  );

  const createKey = useCallback(
    async () => {
      if (!isCreatingKey) {
        setIsCreatingKey(true);
        try {
          const data = await api.mavenKeys.createAPIKey(accountId);
          if (isMounted.current && data && data.key) {
            setKey(data.key);
          }
        } catch (error) {
          addNotification(error, 'error');
        }
      }
      if (isMounted.current) {
        setIsCreatingKey(false);
      }
      try {
        await api.mavenConfig.setServerToServerAuthStatus(accountId, true);
      } catch (error) {
        // do nothing
      }
    }, [accountId, addNotification, isCreatingKey],
  );

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (accountId) {
      getKey();
    }
  }, [accountId, getKey]);

  if (isLoading || isCreatingKey) {
    return (
      <Loading size={24} data-testid="loading-settings" />
    );
  }

  return (
    <Box>
      <Box height="42px">
        <CopyableText text={key || MAVEN_API_KEY_PLACEHOLDER} copyEnabled={!!key} testId="maven-api-key" />
      </Box>

      <Flex mt={4}>
        <Button
          variant="tertiary"
          onClick={key ? regenerateKey : createKey}
          data-testid="reload-button"
        >
          {key ? MAVEN_API_KEY_REGENERATE : MAVEN_API_KEY_GENERATE}
        </Button>
      </Flex>
      <ConfirmModal {...confirmModalProps} />
    </Box>
  );
};

export default MavenApiKey;
